import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { forwardRef, useState, type SelectHTMLAttributes } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, CalendarClock, KeyRound, Mail, Smartphone, Store, UserRound } from "lucide-react";
import {
  adminLicenseSchema,
  adminTenantOwnerPasswordResetSchema,
  adminTenantProfileSchema,
  type BillingEventType,
  type AdminLicenseInput,
  type AdminSyncBlobAggregate,
  type AdminSyncDeviceView,
  type AdminSyncRecentAuditEntry,
  type AdminTenantOwnerPasswordResetInput,
  type AdminTenantProfileInput,
  type AdminTenantSyncOverview,
  type TenantLicensePlan
} from "@shudian/contracts";
import {
  adminGetTenant,
  adminGetTenantSync,
  adminListTenantBillingEvents,
  adminListTenantBillingOrders,
  adminResetTenantOwnerPassword,
  adminRevokeTenantDevice,
  adminRevokeTenantSyncDevice,
  adminUpdateTenantLicense,
  adminUpdateTenantProfile
} from "../api/admin.js";
import { Card } from "../components/ui/Card.js";
import { EntitlementChecklist } from "../components/EntitlementChecklist.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { Button } from "../components/ui/Button.js";
import { Badge } from "../components/ui/Badge.js";
import { Table, TBody, THead, TR, TH, TD, EmptyRow } from "../components/ui/Table.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { Alert } from "../components/ui/Alert.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { cn } from "../lib/cn.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime, formatNumber } from "../lib/format.js";
import {
  billingOrderStatusLabel,
  billingOrderStatusTone,
  billingPeriodLabel,
  billingProviderLabel,
  formatCurrency
} from "../lib/billing.js";
import {
  LICENSE_PLAN_OPTIONS,
  dateTimeInputToIso,
  formatLicensePlan,
  formatDateTimeInput,
  getLicenseExpiryState,
  licenseStatusLabel,
  licenseStatusTone,
  renewLicenseExpiresAt
} from "../lib/license.js";
import {
  billingPlanTemplate
} from "../lib/entitlements.js";

export function TenantDetailPage() {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "tenant", tenantId],
    queryFn: () => adminGetTenant(tenantId),
    enabled: !!tenantId
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载失败";
    return (
      <Alert tone="danger">
        <div>{message}</div>
        <Button size="sm" variant="ghost" className="mt-2" onClick={() => navigate("/tenants")}>
          返回用户列表
        </Button>
      </Alert>
    );
  }
  if (!data) return null;

  const { tenant, quotas } = data;
  const showError = (message: string) => {
    setSuccessMessage(null);
    setErrorMessage(message);
  };
  const refreshTenant = () => {
    setErrorMessage(null);
    void queryClient.invalidateQueries({ queryKey: ["admin", "tenant", tenant.id] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "tenant", tenant.id, "billing-events"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "tenant", tenant.id, "billing-orders"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "billing-orders"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "overview"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
  };

  return (
    <div>
      <PageHeader
        eyebrow="客户运营 / 客户详情"
        title={tenant.name}
        description={`客户 ID ${tenant.id} · 登录邮箱 ${tenant.ownerEmail ?? "未设置"}`}
        actions={
          <Button variant="secondary" onClick={() => navigate("/tenants")}>
            <ArrowLeft className="h-4 w-4" />
            返回客户列表
          </Button>
        }
      />

      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
      {successMessage && <Alert tone="success">{successMessage}</Alert>}

      <section className="my-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="授权状态"
          value={licenseStatusLabel(tenant.license.status)}
          detail={formatLicensePlan(tenant.license.plan)}
          tone={licenseStatusTone(tenant.license.status) === "success" ? "success" : licenseStatusTone(tenant.license.status) === "danger" ? "danger" : "warning"}
          icon={<UserRound className="h-4 w-4" />}
        />
        <MetricCard
          label="到期时间"
          value={getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status).label}
          detail={formatDateTime(tenant.license.expiresAt)}
          tone={getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status).tone}
          icon={<CalendarClock className="h-4 w-4" />}
        />
        <MetricCard
          label="授权设备"
          value={`${formatNumber(tenant.activeDeviceCount)} / ${formatNumber(tenant.license.deviceLimit)}`}
          detail={`${formatNumber(tenant.revokedDeviceCount)} 个已吊销`}
          tone="success"
          icon={<Smartphone className="h-4 w-4" />}
        />
        <MetricCard
          label="店铺额度"
          value={`${formatNumber(tenant.storeCount ?? 0)} / ${formatNumber(tenant.license.storeLimit ?? 50)}`}
          detail="已建店铺 / 套餐上限"
          icon={<Store className="h-4 w-4" />}
        />
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          <Card title="客户基本信息" description={`客户 ID ${tenant.id}`}>
            <ProfileForm
              tenantId={tenant.id}
              name={tenant.name}
              onSuccess={() => {
                setSuccessMessage("用户名称已更新");
                refreshTenant();
              }}
              onError={showError}
            />
          </Card>

          <Card title="授权信息" description="功能权限控制用户可用能力；设备上限控制同时激活数量；店铺上限按套餐限制每个账号可创建的店铺数。">
            <LicenseForm
              tenantId={tenant.id}
              initial={{
                plan: tenant.license.plan,
                status: tenant.license.status,
                entitlements: tenant.license.entitlements,
                deviceLimit: tenant.license.deviceLimit,
                storeLimit: tenant.license.storeLimit ?? 50,
                expiresAt: tenant.license.expiresAt
              }}
              onSuccess={() => {
                setSuccessMessage("授权信息已更新");
                refreshTenant();
              }}
              onError={showError}
            />
          </Card>

          <Card
            title="登录账号"
            description="单账号化：每个账号主体对应唯一一个登录账号；如需重置密码请填写新密码后保存。"
          >
            <LoginAccountInfo tenant={tenant} />
            <LoginAccountPasswordForm
              tenantId={tenant.id}
              onSuccess={() => {
                setSuccessMessage("登录账号密码已重置，原登录态已失效");
                refreshTenant();
              }}
              onError={showError}
            />
          </Card>

          <Card title="授权设备" description="吊销后该设备不可重新激活，并会让相关桌面登录态失效。">
            <DeviceTable
              tenantId={tenant.id}
              devices={data.tenant.devices}
              onSuccess={() => {
                setSuccessMessage("设备已吊销");
                refreshTenant();
              }}
              onError={showError}
            />
          </Card>

          <SyncOverviewSection
            tenantId={tenant.id}
            onSuccess={(message) => setSuccessMessage(message)}
            onError={showError}
          />

          <Card
            title="用量额度"
            description={`${formatLicensePlan(tenant.license.plan)}额度；续费或切换套餐后重置当期已用和预留。`}
          >
            {quotas.length === 0 ? (
              <p className="text-sm text-text-muted">暂无额度记录。</p>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>类型</TH>
                    <TH>套餐额度</TH>
                    <TH>已用</TH>
                    <TH>预留</TH>
                    <TH>剩余</TH>
                  </TR>
                </THead>
                <TBody>
                  {quotas.map((quota) => (
                    <TR key={quota.usageType}>
                      <TD>
                        <div className="text-sm text-text-primary">{formatUsageTypeLabel(quota.usageType)}</div>
                        <div className="font-mono text-xs text-text-muted">{quota.usageType}</div>
                      </TD>
                      <TD>{formatNumber(quota.limit)}</TD>
                      <TD>{formatNumber(quota.committed)}</TD>
                      <TD>{formatNumber(quota.reserved)}</TD>
                      <TD className={quota.remaining <= 0 ? "text-status-danger" : undefined}>
                        {formatNumber(quota.remaining)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>

          <BillingOrderPanel tenantId={tenant.id} />

          <BillingEventPanel tenantId={tenant.id} />
        </div>

        <aside className="space-y-5">
          <SectionSurface>
            <SectionHeader title="客户摘要" description="客服排查和续费跟进时优先查看这里。" />
            <dl className="space-y-3 px-4 py-4 text-sm">
              <SummaryDetail label="客户 ID" value={<span className="font-mono">{tenant.id}</span>} />
              <SummaryDetail label="登录邮箱" value={tenant.ownerEmail ?? "未设置"} icon={<Mail className="h-3.5 w-3.5" />} />
              <SummaryDetail label="套餐" value={formatLicensePlan(tenant.license.plan)} />
              <SummaryDetail label="授权状态" value={<Badge tone={licenseStatusTone(tenant.license.status)}>{licenseStatusLabel(tenant.license.status)}</Badge>} />
              <SummaryDetail label="到期时间" value={formatDateTime(tenant.license.expiresAt)} />
              <SummaryDetail label="功能权限" value={`${tenant.license.entitlements.length} 项`} />
            </dl>
          </SectionSurface>

          <SectionSurface>
            <SectionHeader title="快捷处置" description="常用操作仍在左侧对应模块中完成。" />
            <div className="space-y-2 px-4 py-4">
              <Button variant="secondary" className="w-full justify-center" onClick={() => document.getElementById("expiresAt")?.focus()}>
                <CalendarClock className="h-4 w-4" />
                定位到授权续费
              </Button>
              <Button variant="secondary" className="w-full justify-center" onClick={() => document.getElementById("ownerPassword")?.focus()}>
                <KeyRound className="h-4 w-4" />
                定位到重置密码
              </Button>
            </div>
          </SectionSurface>
        </aside>
      </div>
    </div>
  );
}

function BillingOrderPanel({ tenantId }: { tenantId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "tenant", tenantId, "billing-orders"],
    queryFn: () => adminListTenantBillingOrders(tenantId, { limit: 100 })
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    return (
      <Alert tone="danger">
        {error instanceof CloudApiError ? describeError(error.code) : "加载订单失败"}
      </Alert>
    );
  }

  return (
    <Card bodyClassName="p-0" title="开通订单" description="展示该用户通过支付流程创建的订单，便于核对授权开通来源。">
      <Table className="rounded-none border-0">
        <THead>
          <TR>
            <TH>订单</TH>
            <TH>套餐</TH>
            <TH>金额</TH>
            <TH>状态</TH>
            <TH>支付渠道</TH>
            <TH>创建时间</TH>
            <TH>支付时间</TH>
          </TR>
        </THead>
        <TBody>
          {data && data.orders.length > 0 ? (
            data.orders.map((order) => (
              <TR key={order.id}>
                <TD>
                  <div className="font-mono text-xs text-text-primary">{order.id}</div>
                  <div className="font-mono text-xxs text-text-muted">{order.providerOrderId}</div>
                </TD>
                <TD>
                  <div className="text-sm text-text-primary">{formatLicensePlan(order.licensePlan)}</div>
                  <div className="text-xs text-text-muted">{billingPeriodLabel(order.billingPeriod)}</div>
                </TD>
                <TD className="whitespace-nowrap">{formatCurrency(order.amount, order.currency)}</TD>
                <TD>
                  <Badge tone={billingOrderStatusTone(order.status)}>
                    {billingOrderStatusLabel(order.status)}
                  </Badge>
                </TD>
                <TD>
                  <div className="text-xs text-text-primary">{billingProviderLabel(order.provider)}</div>
                  <div className="font-mono text-xxs text-text-muted">
                    {order.providerTransactionId ?? "-"}
                  </div>
                </TD>
                <TD className="whitespace-nowrap text-xs text-text-secondary">
                  {formatDateTime(order.createdAt)}
                </TD>
                <TD className="whitespace-nowrap text-xs text-text-secondary">
                  {formatDateTime(order.paidAt)}
                </TD>
              </TR>
            ))
          ) : (
            <EmptyRow colSpan={7} label="暂无开通订单。" />
          )}
        </TBody>
      </Table>
    </Card>
  );
}

function BillingEventPanel({ tenantId }: { tenantId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "tenant", tenantId, "billing-events"],
    queryFn: () => adminListTenantBillingEvents(tenantId)
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    return (
      <Alert tone="danger">
        {error instanceof CloudApiError ? describeError(error.code) : "加载账务事件失败"}
      </Alert>
    );
  }

  return (
    <Card bodyClassName="p-0" title="账务事件" description="记录授权、续费、套餐和状态变化，供后续对账与客服追踪。">
      <Table className="rounded-none border-0">
        <THead>
          <TR>
            <TH>时间</TH>
            <TH>事件</TH>
            <TH>套餐</TH>
            <TH>授权状态</TH>
            <TH>到期时间</TH>
            <TH>操作者</TH>
          </TR>
        </THead>
        <TBody>
          {data && data.events.length > 0 ? (
            data.events.map((event) => (
              <TR key={event.id}>
                <TD className="whitespace-nowrap text-xs text-text-secondary">
                  {formatDateTime(event.createdAt)}
                </TD>
                <TD>
                  <div className="text-sm text-text-primary">{billingEventLabel(event.eventType)}</div>
                  <div className="font-mono text-xs text-text-muted">{event.id}</div>
                </TD>
                <TD>{formatLicensePlan(event.plan)}</TD>
                <TD>
                  <Badge tone={licenseStatusTone(event.status)}>{licenseStatusLabel(event.status)}</Badge>
                </TD>
                <TD className="text-xs text-text-secondary">{formatDateTime(event.expiresAt)}</TD>
                <TD className="font-mono text-xs text-text-muted">{event.createdByAdminId ?? "系统"}</TD>
              </TR>
            ))
          ) : (
            <EmptyRow colSpan={6} label="暂无账务事件。" />
          )}
        </TBody>
      </Table>
    </Card>
  );
}

function billingEventLabel(eventType: BillingEventType): string {
  switch (eventType) {
    case "license_created":
      return "创建授权";
    case "license_renewed":
      return "授权续费";
    case "license_plan_changed":
      return "套餐变更";
    case "license_status_changed":
      return "状态变更";
    case "license_updated":
      return "授权更新";
    default:
      return eventType;
  }
}

function formatUsageTypeLabel(usageType: string): string {
  return usageType;
}

function SummaryDetail({
  label,
  value,
  icon
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-surface-border pb-3 last:border-b-0 last:pb-0">
      <dt className="flex items-center gap-1.5 text-xs text-text-muted">
        {icon}
        {label}
      </dt>
      <dd className="min-w-0 text-right text-sm text-text-primary">{value}</dd>
    </div>
  );
}


function ProfileForm({
  tenantId,
  name,
  onSuccess,
  onError
}: {
  tenantId: string;
  name: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm<AdminTenantProfileInput>({
    resolver: zodResolver(adminTenantProfileSchema),
    defaultValues: { name }
  });

  const mutation = useMutation({
    mutationFn: (input: AdminTenantProfileInput) => adminUpdateTenantProfile(tenantId, input),
    onSuccess,
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "更新失败")
  });

  return (
    <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit((values) => mutation.mutate(values))} noValidate>
      <Field label="名称" htmlFor="name" error={errors.name?.message} className="min-w-[260px] flex-1">
        <Input id="name" invalid={!!errors.name} {...register("name")} />
      </Field>
      <Button type="submit" size="sm" disabled={!isDirty || mutation.isPending}>
        {mutation.isPending ? "保存中..." : "保存名称"}
      </Button>
    </form>
  );
}

function LicenseForm({
  tenantId,
  initial,
  onSuccess,
  onError
}: {
  tenantId: string;
  initial: AdminLicenseInput;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [entitlements, setEntitlements] = useState<string[]>(initial.entitlements);
  const expiryState = getLicenseExpiryState(initial.expiresAt, initial.status);
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isDirty }
  } = useForm<AdminLicenseInput>({
    resolver: zodResolver(adminLicenseSchema),
    defaultValues: {
      ...initial,
      expiresAt: formatDateTimeInput(initial.expiresAt)
    }
  });

  const mutation = useMutation({
    mutationFn: (input: AdminLicenseInput) => adminUpdateTenantLicense(tenantId, input),
    onSuccess,
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "更新失败")
  });

  const buildLicenseInput = (values: AdminLicenseInput): AdminLicenseInput => ({
    plan: values.plan,
    status: values.status,
    deviceLimit: Number(values.deviceLimit),
    storeLimit: Number(values.storeLimit),
    entitlements,
    expiresAt: dateTimeInputToIso(values.expiresAt)
  });

  const renew = (plan: TenantLicensePlan) => {
    const values = getValues();
    const template = billingPlanTemplate(billingPlanIdForLicensePlan(plan));
    const expiresAt = renewLicenseExpiresAt(plan, dateTimeInputToIso(values.expiresAt || initial.expiresAt));
    setValue("plan", plan, { shouldDirty: true, shouldValidate: true });
    setValue("status", "active", { shouldDirty: true, shouldValidate: true });
    setValue("deviceLimit", template.deviceLimit, { shouldDirty: true, shouldValidate: true });
    setValue("storeLimit", template.storeLimit, { shouldDirty: true, shouldValidate: true });
    setValue("expiresAt", formatDateTimeInput(expiresAt), { shouldDirty: true, shouldValidate: true });
    setEntitlements(template.entitlements);
    mutation.mutate(
      buildLicenseInput({
        ...values,
        plan,
        status: "active",
        deviceLimit: template.deviceLimit,
        storeLimit: template.storeLimit,
        expiresAt: formatDateTimeInput(expiresAt)
      })
    );
  };

  const applyTemplate = () => {
    const plan = getValues("plan");
    const template = billingPlanTemplate(billingPlanIdForLicensePlan(plan));
    setValue("deviceLimit", template.deviceLimit, { shouldDirty: true, shouldValidate: true });
    setValue("storeLimit", template.storeLimit, { shouldDirty: true, shouldValidate: true });
    setEntitlements(template.entitlements);
  };

  return (
    <form
      className="space-y-4"
      onSubmit={handleSubmit((values) => {
        mutation.mutate(buildLicenseInput(values));
      })}
      noValidate
    >
      <div className="flex flex-wrap items-center gap-2 rounded border border-surface-border bg-surface-raised/40 px-3 py-2 text-xs">
        <Badge tone={licenseStatusTone(initial.status)}>{licenseStatusLabel(initial.status)}</Badge>
        <span className="text-text-secondary">到期时间 {formatDateTime(initial.expiresAt)}</span>
        <Badge tone={expiryState.tone}>{expiryState.label}</Badge>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Field label="套餐" htmlFor="plan" error={errors.plan?.message}>
          <PlanSelect id="plan" {...register("plan")} />
        </Field>
        <Field label="状态" htmlFor="status" error={errors.status?.message}>
          <select
            id="status"
            className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
            {...register("status")}
          >
            <option value="active">可用</option>
            <option value="disabled">停用</option>
            <option value="expired">已过期</option>
          </select>
        </Field>
        <Field
          label="设备上限"
          htmlFor="deviceLimit"
          error={errors.deviceLimit?.message}
          hint="1-50"
        >
          <Input
            id="deviceLimit"
            type="number"
            min={1}
            max={50}
            invalid={!!errors.deviceLimit}
            {...register("deviceLimit", { valueAsNumber: true })}
          />
        </Field>
        <Field
          label="店铺上限"
          htmlFor="storeLimit"
          error={errors.storeLimit?.message}
          hint="0-10000；0 表示禁止该账号新建店铺"
        >
          <Input
            id="storeLimit"
            type="number"
            min={0}
            max={10000}
            step={10}
            invalid={!!errors.storeLimit}
            {...register("storeLimit", { valueAsNumber: true })}
          />
        </Field>
        <Field label="到期时间" htmlFor="expiresAt" error={errors.expiresAt?.message}>
          <Input
            id="expiresAt"
            type="datetime-local"
            invalid={!!errors.expiresAt}
            {...register("expiresAt")}
          />
        </Field>
      </div>
      <Field label="功能权限" hint="运营只需勾选中文能力；系统会保存为桌面端识别的内部权限码。">
        <EntitlementChecklist value={entitlements} onChange={setEntitlements} columns={3} />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={mutation.isPending && !isDirty}>
          {mutation.isPending ? "保存中..." : "保存授权信息"}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={mutation.isPending} onClick={applyTemplate}>
          应用当前套餐推荐能力
        </Button>
        {LICENSE_PLAN_OPTIONS.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant="secondary"
            disabled={mutation.isPending}
            onClick={() => renew(option.value)}
          >
            <CalendarClock className="h-3.5 w-3.5" />
            续费{option.shortLabel}
          </Button>
        ))}
      </div>
    </form>
  );
}

function billingPlanIdForLicensePlan(plan: TenantLicensePlan) {
  if (plan === "quarterly") return "shudian_quarterly";
  if (plan === "yearly") return "shudian_yearly";
  return "shudian_monthly";
}

function LoginAccountInfo({
  tenant
}: {
  tenant: {
    ownerEmail?: string;
    ownerName?: string;
    ownerStatus?: "active" | "disabled";
    ownerPasswordUpdatedAt?: string;
  };
}) {
  return (
    <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
      <div>
        <dt className="text-text-muted text-xs">登录邮箱</dt>
        <dd className="text-text-primary">{tenant.ownerEmail ?? "—"}</dd>
      </div>
      <div>
        <dt className="text-text-muted text-xs">显示名</dt>
        <dd>{tenant.ownerName ?? "—"}</dd>
      </div>
      <div>
        <dt className="text-text-muted text-xs">状态</dt>
        <dd>
          <Badge tone={tenant.ownerStatus === "disabled" ? "neutral" : "success"}>
            {tenant.ownerStatus === "disabled" ? "已禁用" : "活跃"}
          </Badge>
        </dd>
      </div>
      <div>
        <dt className="text-text-muted text-xs">密码上次更新</dt>
        <dd>
          {tenant.ownerPasswordUpdatedAt
            ? formatDateTime(tenant.ownerPasswordUpdatedAt)
            : "—"}
        </dd>
      </div>
    </dl>
  );
}

function LoginAccountPasswordForm({
  tenantId,
  onSuccess,
  onError
}: {
  tenantId: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<AdminTenantOwnerPasswordResetInput>({
    resolver: zodResolver(adminTenantOwnerPasswordResetSchema),
    defaultValues: { password: "" }
  });
  const mutation = useMutation({
    mutationFn: (input: AdminTenantOwnerPasswordResetInput) =>
      adminResetTenantOwnerPassword(tenantId, input),
    onSuccess: () => {
      reset({ password: "" });
      onSuccess();
    },
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "重置登录账号密码失败")
  });
  return (
    <form
      className="mt-4 flex items-end gap-3"
      onSubmit={handleSubmit((values) => mutation.mutate(values))}
    >
      <Field
        label="新密码"
        htmlFor="ownerPassword"
        error={errors.password?.message}
        hint="≥ 8 位"
        className="flex-1"
      >
        <Input
          id="ownerPassword"
          type="password"
          autoComplete="new-password"
          invalid={!!errors.password}
          {...register("password")}
        />
      </Field>
      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        <KeyRound className="h-3.5 w-3.5" />
        {mutation.isPending ? "重置中…" : "重置登录账号密码"}
      </Button>
    </form>
  );
}

const PlanSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function PlanSelect({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          "h-9 w-full rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:border-accent/70 focus:outline-none focus:ring-2 focus:ring-accent/40",
          className
        )}
        {...rest}
      >
        {LICENSE_PLAN_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
);

function DeviceTable({
  tenantId,
  devices,
  onSuccess,
  onError
}: {
  tenantId: string;
  devices: { id: string; status: string; activatedAt: string; lastSeenAt: string; revokedAt?: string; deviceName?: string; platform?: string; appVersion?: string }[];
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: (deviceId: string) => adminRevokeTenantDevice(tenantId, deviceId),
    onSuccess,
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "吊销失败")
  });

  if (devices.length === 0) {
    return <p className="text-sm text-text-muted">暂无设备。</p>;
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>设备</TH>
          <TH>状态</TH>
          <TH>平台</TH>
          <TH>App 版本</TH>
          <TH>激活时间</TH>
          <TH>最后活跃</TH>
          <TH>操作</TH>
        </TR>
      </THead>
      <TBody>
        {devices.map((device) => (
          <TR key={device.id}>
            <TD>
              <div className="text-text-primary">{device.deviceName ?? device.id}</div>
              <div className="font-mono text-xs text-text-muted">{device.id}</div>
            </TD>
            <TD>
              <Badge tone={device.status === "active" ? "success" : "neutral"}>{device.status}</Badge>
            </TD>
            <TD className="text-xs text-text-secondary">{device.platform ?? "-"}</TD>
            <TD className="text-xs text-text-secondary">{device.appVersion ?? "-"}</TD>
            <TD className="text-xs text-text-secondary">{formatDateTime(device.activatedAt)}</TD>
            <TD className="text-xs text-text-secondary">{formatDateTime(device.lastSeenAt)}</TD>
            <TD>
              <Button
                size="sm"
                variant="danger"
                disabled={device.status !== "active" || mutation.isPending}
                onClick={() => {
                  if (window.confirm(`确认吊销设备 ${device.deviceName ?? device.id} ?`)) {
                    mutation.mutate(device.id);
                  }
                }}
              >
                吊销
              </Button>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

// ===== 云同步：概览 / 设备 / 按 kind 用量 / 最近审计 =====

const SYNC_BLOB_KIND_LABEL: Record<string, string> = {
  store_credential_v1: "店铺凭据",
  browser_profile_archive_v1: "浏览器资料归档"
};

const SYNC_AUDIT_ACTION_LABEL: Record<string, string> = {
  "cloud.sync.key_envelope.init": "初始化云同步密钥（历史）",
  "cloud.sync.root_key.fetch": "拉取同步根密钥",
  "cloud.sync.device.register": "注册同步设备",
  "cloud.sync.device.revoke": "吊销同步设备",
  "cloud.sync.push": "上传同步数据",
  "cloud.sync.pull": "拉取同步数据",
  "cloud.sync.blob.upload": "上传同步密文",
  "cloud.sync.blob.download": "下载同步密文",
  "cloud.sync.rejected": "拒绝同步数据",
  "cloud.admin.sync.device.revoke": "管理员吊销同步设备"
};

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GiB`;
}

function SyncOverviewSection({
  tenantId,
  onSuccess,
  onError
}: {
  tenantId: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "tenant", tenantId, "sync"],
    queryFn: () => adminGetTenantSync(tenantId),
    enabled: Boolean(tenantId)
  });

  if (isLoading) {
    return (
      <Card title="云同步" description="单账号同步概览：root_key / 设备 / 密文用量 / 最近审计。">
        <p className="text-sm text-text-muted">加载中…</p>
      </Card>
    );
  }
  if (isError) {
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载同步概览失败";
    return (
      <Card title="云同步" description="单账号同步概览：root_key / 设备 / 密文用量 / 最近审计。">
        <Alert tone="danger">{message}</Alert>
      </Card>
    );
  }
  if (!data) return null;

  const overview = data.overview;
  const handleRefresh = () => {
    void refetch();
  };
  return (
    <Card
      title="云同步"
      description="单账号同步概览：root_key 版本 / 设备 / 密文用量 / 最近 5 条同步审计；不展示明文与完整密钥。"
      actions={
        <Button size="sm" variant="ghost" onClick={handleRefresh}>
          刷新
        </Button>
      }
    >
      <div className="space-y-4">
        <SyncEnvelopeAndKpis overview={overview} />
        <SyncDeviceSection
          tenantId={tenantId}
          devices={overview.devices}
          onSuccess={(message) => {
            onSuccess(message);
            void refetch();
          }}
          onError={onError}
        />
        <SyncBlobAggregateTable aggregates={overview.blobAggregates} />
        <RecentSyncAuditTable entries={overview.recentAudits} />
      </div>
    </Card>
  );
}

function SyncEnvelopeAndKpis({ overview }: { overview: AdminTenantSyncOverview }) {
  const rk = overview.rootKey;
  const activeDeviceCount = overview.devices.filter((device) => device.status === "active").length;
  return (
    <section className="grid gap-4 md:grid-cols-2">
      <div className="rounded border border-border bg-panel-2 p-3 text-sm">
        <div className="text-text-muted">同步根密钥</div>
        {rk.version === null ? (
          <div className="mt-1 text-text-secondary">账号尚未拉取过 root_key（首次登录后惰性创建）。</div>
        ) : (
          <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-text-muted">版本</dt>
            <dd className="font-mono">v{rk.version}</dd>
            <dt className="text-text-muted">master KEK</dt>
            <dd className="font-mono text-xs">{rk.masterKeyId ?? "-"}</dd>
            <dt className="text-text-muted">创建时间</dt>
            <dd>{rk.createdAt ? formatDateTime(rk.createdAt) : "-"}</dd>
            <dt className="text-text-muted">最近轮换</dt>
            <dd>{rk.rotatedAt ? formatDateTime(rk.rotatedAt) : "—"}</dd>
            <dt className="text-text-muted">轮换前版本</dt>
            <dd>{rk.rotatedFromVersion !== null ? `v${rk.rotatedFromVersion}` : "—"}</dd>
          </dl>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SyncKpi label="活跃同步设备" value={String(activeDeviceCount)} sub={`总计 ${overview.devices.length}`} />
        <SyncKpi label="密文 blob" value={formatNumber(overview.blobCount)} sub={formatBytes(overview.totalBlobBytes)} />
        <SyncKpi
          label="按 kind"
          value={String(overview.blobAggregates.length)}
          sub={overview.blobAggregates.map((agg) => SYNC_BLOB_KIND_LABEL[agg.kind] ?? agg.kind).join(" / ") || "—"}
        />
        <SyncKpi
          label="最近同步"
          value={overview.lastSyncedAt ? formatDateTime(overview.lastSyncedAt) : "—"}
          sub="所有 blob 的 max(updatedAt)"
        />
      </div>
    </section>
  );
}

function SyncKpi({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded border border-border bg-panel-2 p-3">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-text-primary">{value}</div>
      <div className="mt-1 text-xs text-text-secondary">{sub}</div>
    </div>
  );
}

function SyncDeviceSection({
  tenantId,
  devices,
  onSuccess,
  onError
}: {
  tenantId: string;
  devices: AdminSyncDeviceView[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: (deviceId: string) => adminRevokeTenantSyncDevice(tenantId, deviceId),
    onSuccess: () => onSuccess("同步设备已吊销"),
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "同步设备吊销失败")
  });
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-text-primary">同步设备</h4>
        <span className="text-xs text-text-muted">公钥仅展示指纹前 16 位 hex</span>
      </div>
      {devices.length === 0 ? (
        <p className="text-sm text-text-muted">暂无同步设备。</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>设备</TH>
              <TH>状态</TH>
              <TH>公钥指纹</TH>
              <TH>注册时间</TH>
              <TH>最近活跃</TH>
              <TH>操作</TH>
            </TR>
          </THead>
          <TBody>
            {devices.map((device) => (
              <TR key={device.id}>
                <TD>
                  <div className="text-text-primary">{device.deviceName ?? device.deviceId}</div>
                  <div className="font-mono text-xs text-text-muted">{device.deviceId}</div>
                </TD>
                <TD>
                  <Badge tone={device.status === "active" ? "success" : "neutral"}>{device.status}</Badge>
                </TD>
                <TD className="font-mono text-xs text-text-secondary">{device.publicKeyJwkThumbprint ?? "—"}</TD>
                <TD className="text-xs text-text-secondary">{formatDateTime(device.trustedAt)}</TD>
                <TD className="text-xs text-text-secondary">{formatDateTime(device.lastSeenAt)}</TD>
                <TD>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={device.status !== "active" || mutation.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `确认吊销同步设备 ${device.deviceName ?? device.deviceId} ?\n\n吊销后该设备将无法再 push / pull 同步数据，桌面端会在 5 秒内清空内存中的 root_key。`
                        )
                      ) {
                        mutation.mutate(device.deviceId);
                      }
                    }}
                  >
                    吊销
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

function SyncBlobAggregateTable({ aggregates }: { aggregates: AdminSyncBlobAggregate[] }) {
  return (
    <section>
      <h4 className="mb-2 text-sm font-semibold text-text-primary">密文用量（按 kind）</h4>
      {aggregates.length === 0 ? (
        <p className="text-sm text-text-muted">该账号尚未上传任何同步密文。</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>类型</TH>
              <TH>数量</TH>
              <TH>占用</TH>
              <TH>最高版本</TH>
              <TH>最近更新</TH>
            </TR>
          </THead>
          <TBody>
            {aggregates.map((agg) => (
              <TR key={agg.kind}>
                <TD>
                  <div className="text-text-primary">{SYNC_BLOB_KIND_LABEL[agg.kind] ?? agg.kind}</div>
                  <div className="font-mono text-xs text-text-muted">{agg.kind}</div>
                </TD>
                <TD>{formatNumber(agg.count)}</TD>
                <TD>{formatBytes(agg.totalBytes)}</TD>
                <TD className="font-mono text-xs">v{agg.latestVersion}</TD>
                <TD className="text-xs text-text-secondary">
                  {agg.latestUpdatedAt ? formatDateTime(agg.latestUpdatedAt) : "—"}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

function RecentSyncAuditTable({ entries }: { entries: AdminSyncRecentAuditEntry[] }) {
  return (
    <section>
      <h4 className="mb-2 text-sm font-semibold text-text-primary">最近同步审计（5 条）</h4>
      {entries.length === 0 ? (
        <p className="text-sm text-text-muted">暂无同步审计。</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>动作</TH>
              <TH>结果</TH>
              <TH>错误码</TH>
              <TH>触发设备</TH>
              <TH>时间</TH>
            </TR>
          </THead>
          <TBody>
            {entries.map((entry) => (
              <TR key={entry.id}>
                <TD>
                  <div className="text-text-primary">
                    {SYNC_AUDIT_ACTION_LABEL[entry.action] ?? entry.action}
                  </div>
                  <div className="font-mono text-xs text-text-muted">{entry.action}</div>
                </TD>
                <TD>
                  <Badge tone={entry.outcome === "succeeded" ? "success" : "warning"}>
                    {entry.outcome}
                  </Badge>
                </TD>
                <TD className="font-mono text-xs text-text-secondary">{entry.errorCode ?? "—"}</TD>
                <TD className="font-mono text-xs text-text-secondary">{entry.deviceId ?? "—"}</TD>
                <TD className="text-xs text-text-secondary">{formatDateTime(entry.createdAt)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}
